import * as React from 'react';
import { Box, Skeleton } from '@mui/material';
import { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { FastField, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';

import { useApi } from '../../shared/context';
import {
	QueryKeys,
	useDealQuestionAnswersQuery,
	useDealQuestionsQuery,
	useNavigateToNextPage,
} from '../../shared/hooks';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { FormikUtils } from '../../shared/utils';
import { DealQuestionAnswer, Guid, Question, SaveDealQuestionAnswerDto } from '../../shared/models';

import {
	CerebroBreadcrumb,
	CerebroForm,
	CerebroHeading,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';

import { DealQuestionYesNo } from './DealQuestionYesNo';
import { DealQuestionOpenEnded } from './DealQuestionOpenEnded';
import { DealQuestionMultipleChoice } from './DealQuestionMultipleChoice';
import { getApiResponseData } from '../../shared/utils/axios';

export const Questions: React.FC = () => {
	const api = useApi();
	const { guid } = useParams<'guid'>();

	const snackbar = useSnackbar();
	const queryClient = useQueryClient();
	const navigateToNextPage = useNavigateToNextPage();

	const questionsQuery = useDealQuestionsQuery();
	const answersQuery = useDealQuestionAnswersQuery(guid);

	const categorizedQuestions: CategorizedQuestion[] = React.useMemo(() => {
		const initial: CategorizedQuestions = {
			other: { categoryName: 'Other', categoryOrder: Number.MAX_VALUE, questionsAndAnswers: [] },
		};

		if (!questionsQuery.isSuccess) return [initial.other];

		const map = questionsQuery.data.reduce<CategorizedQuestions>((categories, question) => {
			const questionAndAnswer: QuestionAndAnswer = {
				question,
				answer: answersQuery.data?.find(answer => answer.questionGuid == question.guid) ?? null,
			};

			if (question.category == null || question.category.guid === undefined) {
				categories.other.questionsAndAnswers.push(questionAndAnswer);
				return categories;
			}

			if (categories[question.category.guid] == undefined) {
				categories[question.category.guid] = {
					categoryName: question.category.name,
					categoryOrder: question.category.order,
					questionsAndAnswers: [],
				};
			}

			categories[question.category.guid].questionsAndAnswers.push(questionAndAnswer);
			return categories;
		}, initial);

		return Object.values(map).sort((categoryA, categoryB) => categoryA.categoryOrder - categoryB.categoryOrder);
	}, [questionsQuery.isSuccess, questionsQuery.data, answersQuery.data]);

	const initialValues = React.useMemo(() => mapToInitialValues(categorizedQuestions), [categorizedQuestions]);

	const { mutate } = useMutation(
		async ({ values }: MutationVariables) => {
			const dtos: SaveDealQuestionAnswerDto[] =
				questionsQuery.data?.map(question => {
					const guid = values[buildFormKey(question, 'answerGuid')];

					return {
						guid: guid !== '' ? guid : null,
						answerText: values[buildFormKey(question, 'text')],
						answerValue: values[buildFormKey(question, 'value')],
						questionGuid: question.guid,
					};
				}) ?? [];

			const { data, error } = await getApiResponseData<DealQuestionAnswer[]>(
				api.post<SaveDealQuestionAnswerDto[], AxiosResponse<DealQuestionAnswer[]>>(
					CerebroApiEndpoints.Questions.Answers(guid),
					dtos
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: answers => {
				queryClient.setQueryData(QueryKeys.Questions.Answers(guid), answers);
				snackbar.enqueueSnackbar(`Progress Saved.`, { variant: 'success' });
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				queryClient.invalidateQueries(QueryKeys.Questions.Answers(guid));
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Questions</CerebroBreadcrumb>

			{questionsQuery.isLoading && <Skeleton height={400} />}

			{questionsQuery.isSuccess && (
				<Formik enableReinitialize initialValues={initialValues} onSubmit={FormikUtils.MutateOnSubmit(mutate)}>
					{({ handleSubmit, values, isSubmitting }) => (
						<CerebroForm sx={{ marginTop: 3 }} onSubmit={handleSubmit}>
							{categorizedQuestions.map(category => {
								return (
									<Box key={category.categoryName} sx={{ marginBottom: 6 }}>
										<CerebroHeading size="h2" component="h3" color="primary">
											{category.categoryName}
										</CerebroHeading>
										{category.questionsAndAnswers
											.sort((a, b) => a.question.questionNumber - b.question.questionNumber)
											.map(({ question }) => {
												const props: QuestionProps = { question, values, isSubmitting };

												return (
													<Box key={question.guid} sx={{ marginY: 3 }}>
														<FastField
															type="hidden"
															name={buildFormKey(question, 'answerGuid')}
														/>

														{question.questionFormat == 'YesNo' && (
															<DealQuestionYesNo {...props} />
														)}
														{question.questionFormat == 'YesNoWithComment' && (
															<DealQuestionYesNo {...props} />
														)}
														{question.questionFormat == 'MultipleChoice' && (
															<DealQuestionMultipleChoice {...props} />
														)}
														{question.questionFormat == 'OpenEnded' && (
															<DealQuestionOpenEnded {...props} />
														)}
													</Box>
												);
											})}
									</Box>
								);
							})}

							<CerebroPrimaryButton type="submit" loading={isSubmitting}>
								Save Changes
							</CerebroPrimaryButton>
						</CerebroForm>
					)}
				</Formik>
			)}
		</CerebroPageContainer>
	);
};

export function buildFormKey(question: Question, type: FormValueType): keyof QuestionsFormValues {
	return `${question.guid}|${type}`;
}

function mapToInitialValues(categorizedQuestions: CategorizedQuestion[]) {
	return categorizedQuestions
		.flatMap(categorizedQuestion => categorizedQuestion.questionsAndAnswers)
		.reduce<QuestionsFormValues>((initialValues, { question, answer }) => {
			initialValues[buildFormKey(question, 'value')] = answer?.answerValue ?? '';
			initialValues[buildFormKey(question, 'text')] = answer?.answerText ?? '';
			initialValues[buildFormKey(question, 'answerGuid')] = answer?.guid ?? '';

			return initialValues;
		}, {});
}

interface CategorizedQuestion {
	categoryName: string;
	categoryOrder: number;
	questionsAndAnswers: QuestionAndAnswer[];
}

interface CategorizedQuestions {
	other: CategorizedQuestion;
	[key: string]: CategorizedQuestion;
}

interface QuestionAndAnswer {
	question: Question;
	answer: DealQuestionAnswer | null;
}

type FormValueType = 'value' | 'text' | 'answerGuid';

interface QuestionsFormValues {
	[key: `${Guid}|${FormValueType}`]: string;
}

type MutationVariables = FormikUtils.MutateOnSubmitVariables<QuestionsFormValues>;

export interface QuestionProps {
	question: Question;
	values: QuestionsFormValues;
	isSubmitting: boolean;
}
